import React, { useState, useEffect, useRef } from "react";
import { FaSearch } from "react-icons/fa";

const DashboardHeader = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [editProfile, setEditProfile] = useState({
    name: "",
    email: "",
    role: "",
    profile_picture: null,
    password: "",
    confirm_password: "",
  });
  const modalRef = useRef(null); // Ref for profile modal
  const dropdownRef = useRef(null); // Ref for dropdown menu

  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem("authToken");

      if (!token) {
        setError("Authentication token missing. Please log in again.");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          "https://xebrand.in/Backend/getUserProfile.php",
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const result = await response.json();

        if (response.ok && result.success) {
          setUser(result.data);
          setEditProfile({
            name: result.data.name,
            email: result.data.email,
            role: result.data.role,
            profile_picture: null,
            password: "",
            confirm_password: "",
          });
        } else {
          setError(result.message || "Failed to fetch user data.");
        }
      } catch (error) {
        setError("An error occurred while fetching user data.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close profile modal if clicked outside
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setProfileModalOpen(false);
      }

      // Close dropdown if clicked outside
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    window.location.href = "/login";
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setEditProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setEditProfile((prev) => ({
      ...prev,
      profile_picture: e.target.files[0],
    }));
  };

  const handleProfileUpdate = async () => {
    const token = localStorage.getItem("authToken");

    if (!token) {
      alert("Authentication token is missing. Please log in again.");
      return;
    }

    const formData = new FormData();
    formData.append("name", editProfile.name);
    formData.append("email", editProfile.email);
    formData.append("role", editProfile.role);
    if (editProfile.profile_picture) {
      formData.append("profileImage", editProfile.profile_picture);
    }
    if (editProfile.password) {
      if (editProfile.password !== editProfile.confirm_password) {
        alert("Passwords do not match.");
        return;
      }
      formData.append("password", editProfile.password);
    }

    try {
      const response = await fetch(
        "https://xebrand.in/Backend/updateUserProfile.php",
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();
      if (result.success) {
        alert("Profile updated successfully!");
        setProfileModalOpen(false);
        setUser((prev) => ({
          ...prev,
          name: editProfile.name,
          email: editProfile.email,
          role: editProfile.role,
        }));
      } else {
        alert(result.message || "Failed to update profile.");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  const getRandomColor = () => {
    const colors = ["bg-red-500", "bg-green-500", "bg-blue-500", "bg-yellow-500"];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-16 bg-red-50">
        <p className="text-sm text-red-500">{error}</p>
        <button
          onClick={handleLogout}
          className="text-sm text-blue-500 underline"
        >
          Logout
        </button>
      </div>
    );
  }

  if (!user) {
    return <div>Error: Unable to load user data</div>;
  }

  return (
    <div className="flex items-center justify-between px-6 py-4">
      <div>
        <h1 className="text-xl font-bold text-gray-800">Dashboard</h1>
        <p className="text-sm text-gray-500">
          {new Date().toLocaleDateString("en-US", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })}
        </p>
      </div>

      <div className="flex items-center bg-gray-100 rounded-full px-4 py-2 w-1/3 shadow-inner">
        <FaSearch className="text-gray-500 mr-2" />
        <input
          type="text"
          placeholder="Search"
          className="bg-transparent outline-none text-gray-700 w-full"
        />
      </div>

      <div className="flex items-center space-x-4 relative">
        <div ref={dropdownRef} className="relative">
          <button
            className="flex items-center space-x-2"
            onClick={() => setDropdownOpen((prev) => !prev)}
          >
            {user.profile_picture ? (
              <img
                src={user.profile_picture}
                alt="Profile"
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div
                className={`w-10 h-10 flex items-center justify-center rounded-full text-white font-bold ${getRandomColor()}`}
              >
                {user.name.charAt(0).toUpperCase()}
              </div>
            )}
            <span className="text-gray-800 font-medium">{user.name}</span>
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white shadow-md rounded-md overflow-hidden z-10">
              <button
                onClick={() => setProfileModalOpen(true)}
                className="block w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100"
              >
                My Profile
              </button>
              <button
                onClick={handleLogout}
                className="block w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {profileModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20"
        >
          <div
            ref={modalRef}
            className="bg-white p-6 rounded-lg w-1/3"
          >
            <h2 className="text-lg font-bold mb-4">My Profile</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Name
              </label>
              <input
                type="text"
                name="name"
                value={editProfile.name}
                onChange={handleProfileChange}
                className="w-full border rounded px-3 py-2 mt-1"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={editProfile.email}
                onChange={handleProfileChange}
                className="w-full border rounded px-3 py-2 mt-1"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Role
              </label>
              <input
                type="text"
                name="role"
                value={editProfile.role}
                onChange={handleProfileChange}
                className="w-full border rounded px-3 py-2 mt-1"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Profile Picture
              </label>
              <input
                type="file"
                name="profile_picture"
                onChange={handleFileChange}
                className="w-full border rounded px-3 py-2 mt-1"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                type="password"
                name="password"
                value={editProfile.password}
                onChange={handleProfileChange}
                className="w-full border rounded px-3 py-2 mt-1"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <input
                type="password"
                name="confirm_password"
                value={editProfile.confirm_password}
                onChange={handleProfileChange}
                className="w-full border rounded px-3 py-2 mt-1"
              />
            </div>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setProfileModalOpen(false)}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleProfileUpdate}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardHeader;
